import React, { useState } from 'react';
import { Box, Typography, Button } from '@mui/material';
import { FacetGroup } from './FacetGroup';
import { CheckboxFacet } from './CheckboxFacet';
import type { FacetBucket } from '../../contexts/SearchContext';

export type FacetKey = 'workType' | 'materials' | 'techniques' | 'creators' | 'decade';

// Order mirrors the Getty Vocab entry order in the upload/edit wizard's
// Media tab (Work Type -> Techniques -> Materials -> Substrate), so search
// filters present facets the way users already expect from cataloging
// (GH#1264). Substrate has no corresponding search facet yet.
const FACET_LABELS: Record<FacetKey, string> = {
  workType: 'Work type',
  techniques: 'Techniques',
  materials: 'Materials',
  creators: 'Creator',
  decade: 'Creation year',
};

export interface SearchFilterRailProps {
  /** The available facet buckets from the backend, keyed by facet name. */
  buckets: Record<string, FacetBucket[]> | null;
  /** Currently-selected values per facet. */
  selected: Record<FacetKey, Set<string>>;
  onToggle: (facet: FacetKey, value: string) => void;
  onClearAll: () => void;
  /** Extra content rendered above the facet groups (e.g. catalog tree). */
  topSlot?: React.ReactNode;
}

/**
 * Persistent left filter rail. Each facet group is independently collapsible;
 * groups with a non-empty selection show a dot + count badge in the header
 * (mirroring the design's "active" state).
 *
 * This is Phase 3b's minimum-viable rail — it renders the five Getty/creator
 * facets from backend aggregations. Catalog tree, provenance text, and
 * dimension range are intentionally out of scope here and will land as
 * dedicated follow-up atoms.
 */
export const SearchFilterRail: React.FC<SearchFilterRailProps> = ({
  buckets,
  selected,
  onToggle,
  onClearAll,
  topSlot,
}) => {
  const initialOpen: Record<FacetKey, boolean> = {
    workType: true,
    techniques: false,
    materials: false,
    creators: false,
    decade: false,
  };
  const [openGroups, setOpenGroups] = useState(initialOpen);

  const activeTotal = (Object.keys(selected) as FacetKey[]).reduce(
    (sum, key) => sum + selected[key].size,
    0,
  );

  return (
    <Box
      component="aside"
      sx={{
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        boxShadow: 1,
        position: 'sticky',
        top: 80,
        maxHeight: 'calc(100vh - 100px)',
        overflow: 'auto',
      }}
      data-testid="search-filter-rail"
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          px: 2.25,
          pt: 2,
          pb: 1.5,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Typography
          component="h3"
          sx={{
            fontFamily: 'serif',
            fontWeight: 700,
            fontSize: '1.0625rem',
            color: 'primary.main',
            m: 0,
            display: 'flex',
            alignItems: 'baseline',
            gap: 1,
          }}
        >
          Filter
          {activeTotal > 0 && (
            <Box
              component="span"
              sx={{
                bgcolor: 'primary.main',
                color: 'white',
                borderRadius: '999px',
                px: 0.875,
                py: 0.375,
                fontSize: '0.6875rem',
                fontWeight: 600,
              }}
            >
              {activeTotal}
            </Box>
          )}
        </Typography>
        <Button
          size="small"
          onClick={onClearAll}
          disabled={activeTotal === 0}
          sx={{
            fontSize: '0.8125rem',
            fontWeight: 600,
            color: activeTotal > 0 ? 'primary.main' : 'text.secondary',
            textTransform: 'none',
            minWidth: 0,
            p: '2px 4px',
          }}
        >
          Clear all
        </Button>
      </Box>

      {topSlot}

      {(Object.keys(FACET_LABELS) as FacetKey[]).map((key) => (
        <FacetGroup
          key={key}
          label={FACET_LABELS[key]}
          activeCount={selected[key].size}
          open={openGroups[key]}
          onToggle={() => setOpenGroups((prev) => ({ ...prev, [key]: !prev[key] }))}
        >
          <CheckboxFacet
            buckets={buckets?.[key] ?? []}
            selected={selected[key]}
            onToggle={(value) => onToggle(key, value)}
          />
        </FacetGroup>
      ))}
    </Box>
  );
};

export default SearchFilterRail;
